import React, { useState } from 'react';
import { App, Button, Card, Form, Input, Modal, Popconfirm, Space, Switch, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api.service';

const { Title } = Typography;

interface SupplierCategoryRow {
  id: string;
  category_name: string;
  description?: string;
  is_active: boolean;
}

function unwrap<T>(res: unknown): T {
  const data = (res as any)?.data ?? res;
  return (data?.data ?? data) as T;
}

const QUERY_KEY = ['procurement-supplier-categories-master'];

const SupplierCategoryMasterPage: React.FC = () => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [editing, setEditing] = useState<SupplierCategoryRow | null>(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await api.get('/procurement/supplier-categories?only_active=false');
      const list = unwrap<SupplierCategoryRow[]>(res);
      return Array.isArray(list) ? list : [];
    },
  });

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true });
    setOpen(true);
  };

  const openEdit = (row: SupplierCategoryRow) => {
    setEditing(row);
    form.setFieldsValue({
      category_name: row.category_name,
      description: row.description,
      is_active: row.is_active,
    });
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editing) {
        await api.put(`/procurement/supplier-categories/${editing.id}`, values);
        message.success('Supplier category updated');
      } else {
        await api.post('/procurement/supplier-categories', values);
        message.success('Supplier category created');
      }
      setOpen(false);
      setEditing(null);
      form.resetFields();
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: ['procurement-supplier-categories'] });
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error?.response?.data?.message || error?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/procurement/supplier-categories/${id}`);
      message.success('Supplier category deleted');
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: ['procurement-supplier-categories'] });
    } catch (error: any) {
      message.error(error?.response?.data?.message || error?.message || 'Delete failed');
    }
  };

  const columns: ColumnsType<SupplierCategoryRow> = [
    { title: 'Category', dataIndex: 'category_name', key: 'category_name' },
    { title: 'Description', dataIndex: 'description', key: 'description' },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (v: boolean) => (v ? 'Active' : 'Inactive'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, row) => (
        <Space>
          <Button size="small" onClick={() => openEdit(row)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete this category?"
            onConfirm={() => handleDelete(row.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={2} style={{ margin: 0 }}>
          Supplier Category Master
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Add category
        </Button>
      </div>

      <Card>
        <Table rowKey="id" columns={columns} dataSource={rows} loading={isLoading} pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }} />
      </Card>

      <Modal
        title={editing ? 'Edit supplier category' : 'New supplier category'}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={handleSave}
        confirmLoading={saving}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="category_name" label="Category name" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="e.g. Raw Materials" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input placeholder="Optional description" />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SupplierCategoryMasterPage;
